/**
 * Reports & Analytics (Traci's executive page, v108/v109; Trera 22 Sep 2026: "this is an executive page for her to
 * track business ... they should read the same thing just like a source of truth").
 *
 * Pure: rows in, the whole report out. Every number is computed here from the same tables and the same balance view
 * the Billing workbench and the staging dashboard read, so no two screens can disagree. Each card carries `rule`,
 * the sentence that says what it counts, and cards the data cannot support yet come back as `pending` with the
 * reason, never as a guessed number (handoff §20).
 *
 * Inputs (all arrays of plain rows; the route fetches them, tests pass fixtures)
 *   range      { from, to }  YYYY-MM-DD, inclusive, in the business time zone; `to` is "today" for as-of numbers
 *   bookings   id, created_at, event_date, booking_status, cancelled_at, updated_at, package_type, hours_booked,
 *              payment_plan, location_city, location_state, photographer_id, videographer_id, second_photographer_id,
 *              media_uploaded_at, media_delivered_at, talent_rejected, logistics_completed, failed_payment_count,
 *              is_test, personalization_data
 *   balances   booking_balances rows (booking_id, agreed_cents, paid_cents, remaining_cents, overpaid_cents,
 *              final_due_date, is_active, is_placeholder, needs_backfill, booking_status, event_date)
 *   payments   amount, payment_date, created_at, status, booking_id
 *   refunds    amount_cents, refunded_at, created_at, status, adjustment_type, booking_id
 *   reviews    rating, created_at, is_verified, booking_id, photographer_id
 *   addonRows  booking_addons joined: booking_id, purchased_at, total_price_cents, addon: { code, name }
 *   talent     photographers: id, status, is_public, city, state, is_videographer
 *   applications talent_applications: is_accepted, submitted_at
 *   questionnaires logistics_questionnaire: booking_id, status (submitted / in_progress / not_started). The booking
 *              column logistics_completed is never set in production; this table is the truth.
 */
import { normalizePackageType } from '../constants/packagePricing.js'
import { talentStatus } from '../staging/talentStatus.js'

const dayMs = 86400000
const dateOnly = v => (v ? String(v).slice(0, 10) : '')
const noon = iso => Date.parse(iso + 'T12:00:00Z')
const addDays = (iso, n) => new Date(noon(iso) + n * dayMs).toISOString().slice(0, 10)
const daysBetween = (a, b) => Math.round((noon(b) - noon(a)) / dayMs)
/**
 * The business day a value falls on (Traci, Oct 2026: "bookings this month is a tad off"). Timestamps are stored in
 * UTC, so a booking made on the evening of 30 Sep in Mountain time counted as October. A plain date stays as it is.
 */
const MOUNTAIN = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit' })
export function businessDate(v) {
  const s = v ? String(v) : ''
  if (s.length <= 10) return s.slice(0, 10)
  const t = Date.parse(s)
  return Number.isFinite(t) ? MOUNTAIN.format(new Date(t)) : s.slice(0, 10)
}
const inRange = (v, range) => { const d = businessDate(v); return Boolean(d) && d >= range.from && d <= range.to }
const PAID = new Set(['completed', 'succeeded', 'paid'])
const CLOSED = new Set(['cancelled', 'canceled', 'completed', 'archived'])
const PLACEHOLDER_TALENT = new Set(['410935f9-6034-40b3-a65f-e9863de60e58', '7c7191dc-c5e5-43ed-b772-083463a00aa4'])
const sum = (arr, f) => arr.reduce((s, x) => s + (Number(f(x)) || 0), 0)
const mean = arr => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null)
const median = arr => { if (!arr.length) return null; const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2 }
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : null)
const round1 = n => (n === null || n === undefined ? null : Math.round(n * 10) / 10)

/** The equal-length period just before `range`, for the "vs prior period" deltas. */
export function priorRange(range) {
  const len = daysBetween(range.from, range.to)
  const to = addDays(range.from, -1)
  return { from: addDays(to, -len), to }
}

/** Preset ranges as of `today` (YYYY-MM-DD): MTD, last 30 days, QTD, YTD. */
export function presetRange(key, today) {
  const y = today.slice(0, 4), m = Number(today.slice(5, 7))
  if (key === 'mtd') return { from: today.slice(0, 8) + '01', to: today }
  if (key === 'qtd') return { from: `${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01`, to: today }
  if (key === 'ytd') return { from: `${y}-01-01`, to: today }
  return { from: addDays(today, -29), to: today }
}

function card(key, label, value, rule, extra = {}) { return { key, label, value, rule, ...extra } }
function pending(key, label, reason) { return { key, label, value: null, pending: true, rule: reason } }
function delta(cur, prev) { return prev === null || prev === undefined || prev === 0 || cur === null ? null : Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10 }

/** Bookings that count anywhere in this report: not test rows, not Rose placeholders. */
export function isRealBooking(b, balById) {
  if (!b || b.is_test) return false
  const bal = balById.get(b.id)
  if (bal?.is_placeholder) return false
  return !(dateOnly(b.event_date) >= '2099-01-01')
}

const marketOf = b => [b.location_city, b.location_state].filter(Boolean).join(', ') || null
const cityKey = (city, state) => `${String(city || '').trim().toLowerCase()}|${String(state || '').trim().toLowerCase()}`
const hasRealPhotographer = b => Boolean(b.photographer_id) && !PLACEHOLDER_TALENT.has(b.photographer_id)
const hasRealVideographer = b => Boolean(b.videographer_id) && !PLACEHOLDER_TALENT.has(b.videographer_id)
const needsVideo = b => ['photoVideo', 'videoOnly'].includes(normalizePackageType(b.package_type))
/** The date a cancelled booking was cancelled (cancelled_at, else its last update), '' when not cancelled. */
export const cancelledOn = b => (String(b.booking_status || '').toLowerCase().startsWith('cancel') ? businessDate(b.cancelled_at || b.updated_at) : '')
/** A payment that is money in: completed / succeeded / paid. */
export const isPaidPayment = p => PAID.has(String(p?.status || '').toLowerCase())
/** A refund that is cash out (price reductions are not refunds). */
export const isCashRefund = x => String(x?.status || '').toLowerCase() === 'succeeded' && (!x.adjustment_type || ['cash_refund', 'cash_and_price_reduction'].includes(x.adjustment_type))

export function computeReport({ range, bookings = [], balances = [], payments = [], refunds = [], reviews = [], addonRows = [], talent = [], applications = [], questionnaires = [] }) {
  const qStatus = new Map(questionnaires.map(q => [q.booking_id, String(q.status || '').toLowerCase()]))
  const prior = priorRange(range)
  const balById = new Map(balances.map(b => [b.booking_id, b]))
  const real = bookings.filter(b => isRealBooking(b, balById))
  const byId = new Map(real.map(b => [b.id, b]))
  const agreedCents = b => Number(balById.get(b.id)?.agreed_cents || 0)
  const today = range.to

  // ── period slices ──
  const createdIn = r => real.filter(b => inRange(b.created_at, r))
  const eventsIn = r => real.filter(b => inRange(b.event_date, r) && !String(b.booking_status || '').toLowerCase().startsWith('cancel'))
  const paidIn = r => payments.filter(p => isPaidPayment(p) && inRange(p.payment_date || p.created_at, r) && byId.has(p.booking_id))
  const refundsIn = r => refunds.filter(x => isCashRefund(x) && inRange(x.refunded_at || x.created_at, r))
  const cancelledIn = r => real.filter(b => inRange(cancelledOn(b), r))
  const reviewsIn = r => reviews.filter(x => x.is_verified !== false && Number(x.rating) > 0 && inRange(x.created_at, r))

  const cur = { created: createdIn(range), events: eventsIn(range), paid: paidIn(range), refunds: refundsIn(range), cancelled: cancelledIn(range), reviews: reviewsIn(range) }
  const prv = { created: createdIn(prior), events: eventsIn(prior), paid: paidIn(prior), refunds: refundsIn(prior), cancelled: cancelledIn(prior), reviews: reviewsIn(prior) }

  const cashCents = ps => Math.round(sum(ps, p => p.amount) * 100)
  const avgValueCents = bs => (bs.length ? Math.round(sum(bs, agreedCents) / bs.length) : null)
  const cancelRate = s => pct(s.cancelled.length, s.created.length + s.cancelled.length - s.cancelled.filter(b => inRange(b.created_at, range)).length)

  // the balance view carries event_date and status too; the booking row wins when both exist (one source per fact)
  const active = balances
    .filter(b => b.is_active !== false && !b.is_placeholder && byId.has(b.booking_id))
    .map(b => ({ ...b, event_date: byId.get(b.booking_id)?.event_date || b.event_date, booking_status: byId.get(b.booking_id)?.booking_status || b.booking_status }))
    .filter(b => !CLOSED.has(String(b.booking_status || '').toLowerCase()))
  const upcoming31 = active.filter(b => { const d = dateOnly(b.event_date); return d >= today && d <= addDays(today, 31) })
  const upcoming7 = active.filter(b => { const d = dateOnly(b.event_date); return d >= today && d <= addDays(today, 7) })
  const owing = active.filter(b => Number(b.remaining_cents || 0) > 0 && !b.needs_backfill)
  const arCents = sum(owing, b => b.remaining_cents)
  const overdue = owing.filter(b => dateOnly(b.final_due_date) && dateOnly(b.final_due_date) < today)
  const overdueDays = b => daysBetween(dateOnly(b.final_due_date), today)

  // ── executive ──
  const avgRating = rs => round1(mean(rs.map(r => Number(r.rating))))
  const executive = {
    cards: [
      card('new_bookings', 'New Bookings', cur.created.length, 'bookings created in the period, test rows and placeholders excluded', { delta: delta(cur.created.length, prv.created.length), prior: prv.created.length }),
      card('cash_collected', 'Cash Collected', cashCents(cur.paid), 'completed payments dated in the period, on real bookings', { money: true, delta: delta(cashCents(cur.paid), cashCents(prv.paid)), prior: cashCents(prv.paid) }),
      card('avg_booking_value', 'Avg Booking Value', avgValueCents(cur.created), 'mean agreed price (balance view) of bookings created in the period', { money: true, delta: delta(avgValueCents(cur.created), avgValueCents(prv.created)), prior: avgValueCents(prv.created) }),
      card('cancellation_rate', 'Cancellation Rate', cancelRate(cur), 'bookings cancelled in the period ÷ bookings created in the period (plus earlier bookings cancelled in it)', { percent: true, delta: delta(cancelRate(cur), cancelRate(prv)), prior: cancelRate(prv) }),
      card('upcoming_events', 'Upcoming Events', upcoming31.length, 'active bookings with an event in the next 31 days', { hint: 'as of the end of the range' }),
      card('outstanding_ar', 'Outstanding A/R', arCents, 'remaining balance across active bookings, balance-view formula; unrecorded-in-Stripe bookings excluded', { money: true, hint: `${overdue.length} overdue balance${overdue.length === 1 ? '' : 's'}` })
    ],
    trend: weeklyTrend(range, cur.created, cur.paid),
    watchlist: [
      { key: 'overdue_balances', text: `${overdue.length} overdue client balance${overdue.length === 1 ? '' : 's'}`, detail: `${overdue.filter(b => overdueDays(b) > 15).length} exceed 15 days overdue`, value: sum(overdue, b => b.remaining_cents), money: true, severity: overdue.length ? 'red' : 'green', to: 'billing' },
      { key: 'thin_markets', text: null, detail: null, severity: 'amber', to: 'coordination' },
      { key: 'not_ready_7', text: null, detail: 'within the next 7 days', severity: 'amber', to: 'coordination' },
      { key: 'review_rating', text: avgRating(cur.reviews) === null ? 'No verified reviews this period' : `Review rating ${avgRating(cur.reviews) >= 4.5 ? 'remains strong' : 'needs attention'}`, detail: `${cur.reviews.length} verified review${cur.reviews.length === 1 ? '' : 's'} this period`, value: avgRating(cur.reviews), severity: avgRating(cur.reviews) === null || avgRating(cur.reviews) >= 4.5 ? 'green' : 'amber', to: 'reviews' }
    ]
  }

  // ── sales & bookings ──
  const typeLabel = { photo: 'Photo Only', photoVideo: 'Photo + Video', videoOnly: 'Video Only' }
  const coverageMix = mix(cur.created, b => typeLabel[normalizePackageType(b.package_type)] || 'Other (engagement, hourly)')
  const leadMonths = cur.created.map(b => daysBetween(dateOnly(b.created_at), dateOnly(b.event_date)) / 30.44).filter(n => Number.isFinite(n) && n >= 0)
  const leadBuckets = mix(leadMonths, m => (m < 3 ? '0–3 months' : m < 6 ? '3–6 months' : m < 9 ? '6–9 months' : '9+ months'), ['0–3 months', '3–6 months', '6–9 months', '9+ months'])
  const planLabel = p => (p === 'full' ? 'Paid in Full' : p === 'deposit500' ? '$500 Deposit' : p === 'monthly99' || p === 'monthly199' ? 'Love & Pay Later' : 'No plan recorded')
  const planMix = mix(cur.created, b => planLabel(b.payment_plan), ['Paid in Full', '$500 Deposit', 'Love & Pay Later'])
  const hoursMix = mix(cur.created.filter(b => Number(b.hours_booked) > 0), b => { const h = Number(b.hours_booked); return h <= 4 ? '≤4h' : h <= 6 ? '5–6h' : h <= 8 ? '7–8h' : h <= 10 ? '9–10h' : '11–12h' }, ['≤4h', '5–6h', '7–8h', '9–10h', '11–12h'])
  const sales = {
    coverageMix, leadTime: { averageMonths: round1(mean(leadMonths)), medianMonths: round1(median(leadMonths)), buckets: leadBuckets }, planMix, hoursMix,
    rule: 'all four from bookings created in the period'
  }

  // ── revenue & billing health ──
  const newBookingRevenueCents = sum(cur.created, agreedCents)
  const failedPayments = payments.filter(p => String(p.status || '').toLowerCase() === 'failed' && inRange(p.payment_date || p.created_at, range) && byId.has(p.booking_id)).length
  const addonRevenue = addonRevenueTable(cur.created, addonRows.filter(r => inRange(r.purchased_at || r.created_at, range) && byId.has(r.booking_id)))
  const aging = [
    { key: 'current', label: 'Current', cents: sum(owing.filter(b => !dateOnly(b.final_due_date) || dateOnly(b.final_due_date) >= today), b => b.remaining_cents) },
    { key: 'd1_7', label: '1–7 days overdue', cents: sum(overdue.filter(b => overdueDays(b) <= 7), b => b.remaining_cents) },
    { key: 'd8_15', label: '8–15 days overdue', cents: sum(overdue.filter(b => overdueDays(b) > 7 && overdueDays(b) <= 15), b => b.remaining_cents) },
    { key: 'd16', label: '16+ days overdue', cents: sum(overdue.filter(b => overdueDays(b) > 15), b => b.remaining_cents) }
  ]
  const revenue = {
    cards: [
      card('cash_collected', 'Cash Collected', cashCents(cur.paid), 'completed payments dated in the period', { money: true }),
      card('new_booking_revenue', 'New Booking Revenue', newBookingRevenueCents, 'agreed price of bookings created in the period (booked, not yet all collected)', { money: true }),
      card('refunds', 'Refunds', sum(cur.refunds, r => r.amount_cents), 'cash refunds succeeded in the period (price reductions are not refunds)', { money: true }),
      card('outstanding_balances', 'Outstanding Balances', arCents, 'same number as Outstanding A/R above', { money: true }),
      card('failed_late', 'Failed / Late', failedPayments + overdue.length, `${failedPayments} failed payment${failedPayments === 1 ? '' : 's'} in the period + ${overdue.length} balance${overdue.length === 1 ? '' : 's'} past due`, {})
    ],
    addonRevenue, aging,
    note: 'Every figure uses the transaction ledger and the balance view locked during Billing.'
  }

  // ── operations health ──
  const pastEvents = cur.events.filter(b => dateOnly(b.event_date) <= today)
  const questionnaireDone = b => qStatus.get(b.id) === 'submitted' || b.logistics_completed === true
  const readyBy = b => questionnaireDone(b) && hasRealPhotographer(b) && (!needsVideo(b) || hasRealVideographer(b))
  const readyOnTime = pct(pastEvents.filter(readyBy).length, pastEvents.length)
  const exceptions7 = upcoming7.map(b => byId.get(b.booking_id)).filter(b => b && !readyBy(b))
  const lateUploads = pastEvents.filter(b => { const up = dateOnly(b.media_uploaded_at); const ev = dateOnly(b.event_date); return up ? daysBetween(ev, up) > 3 : daysBetween(ev, today) > 3 })
  const deliveredAt = b => b.media_delivered_at || b.photo_delivered_at || null
  const deliveredIn = real.filter(b => inRange(deliveredAt(b), range) && dateOnly(b.event_date))
  const avgDelivery = round1(mean(deliveredIn.map(b => daysBetween(dateOnly(b.event_date), dateOnly(deliveredAt(b))))))
  const operations = {
    cards: [
      card('ready_on_time', 'Ready on Time', readyOnTime, 'events in the period whose logistics questionnaire was submitted and that had real talent in every needed slot; an approximation until the new lifecycle defines Ready', { percent: true, approx: true }),
      card('exceptions_7', '7-Day Exceptions', exceptions7.length, 'events in the next 7 days not yet ready by the same rule', {}),
      pending('reassignment_rate', 'Reassignment Rate', 'needs the talent assignment record (Track C); stage history does not name the person replaced'),
      pending('late_check_ins', 'Late Check-ins', 'Nautica\'s 5-day check-in is recorded since 18 Sep 2026; enough history from mid-October'),
      card('late_media_uploads', 'Late Media Uploads', lateUploads.length, 'events in the period with media uploaded more than 3 days after the event, or still missing after 3 days', {}),
      avgDelivery === null
        ? pending('avg_delivery', 'Avg Delivery', 'no delivery date is stamped when Editing marks a booking Delivered today; the Editing build (Track C) records it, and this card fills in from then')
        : card('avg_delivery', 'Avg Delivery', avgDelivery, 'days from event to media delivered, for deliveries in the period', { unit: 'days' })
    ],
    departments: pending('department_health', 'Department Operations Health', 'counts open Flags per department; the Flag entity is a Track C build'),
    flags: pending('flag_trend', 'Flag Trend', 'open flags and median resolution need the Flag entity (Track C)')
  }

  // ── talent health ──
  // The Talent page's rule (talentStatus): Blacklisted, Incomplete (no city/state or profile not complete), Good Standing
  const statusOf = t => t.console_status || talentStatus(t)
  const good = talent.filter(t => statusOf(t) === 'Good Standing')
  const incomplete = talent.filter(t => statusOf(t) === 'Incomplete')
  const black = talent.filter(t => statusOf(t) === 'Blacklisted')
  const approved = applications.filter(a => a.is_accepted === true && inRange(a.submitted_at, range))
  const workedIds = new Set(cur.events.flatMap(b => [b.photographer_id, b.videographer_id, b.second_photographer_id, b.second_videographer_id]).filter(id => id && !PLACEHOLDER_TALENT.has(id)))
  const publicGood = good.filter(t => t.is_public)
  const utilization = pct([...workedIds].filter(id => publicGood.some(t => t.id === id)).length, publicGood.length)
  const talentCancels = cur.events.filter(b => b.talent_rejected).length
  const lateRate = pct(lateUploads.length, pastEvents.length)
  const markets = geographic(cur.created, real, active, talent, agreedCents, today, byId)
  const talentHealth = {
    cards: [
      card('good_standing', 'Good Standing', good.length, 'talent in Good Standing, as on the Talent page', {}),
      card('incomplete', 'Incomplete', incomplete.length, 'talent missing a city and state or a completed profile, as on the Talent page', {}),
      card('approved', 'Approved', approved.length, 'talent applications accepted in the period', {}),
      card('blacklisted', 'Blacklisted', black.length, 'talent blacklisted, as on the Talent page', {}),
      card('avg_rating', 'Avg Rating', avgRating(cur.reviews), 'verified reviews in the period', { unit: '/5' }),
      card('utilization', 'Utilization', utilization, 'public talent in good standing who worked an event in the period ÷ all public talent in good standing', { percent: true })
    ],
    demandVsSupply: markets.filter(m => m.health === 'recruit').slice(0, 3),
    quality: [
      card('avg_talent_rating', 'Avg Talent Rating', avgRating(cur.reviews), 'same as above', { unit: '/5' }),
      card('talent_cancellation_rate', 'Talent Cancellation Rate', pct(talentCancels, cur.events.length), 'events in the period where the assigned talent declined after assignment', { percent: true }),
      pending('admin_reassignment_rate', 'Admin Reassignment Rate', 'needs the assignment record (Track C)'),
      card('late_media_rate', 'Late Media Rate', lateRate, 'late uploads ÷ past events in the period', { percent: true })
    ]
  }

  // ── client experience ──
  const negative = cur.reviews.filter(r => Number(r.rating) <= 2).length
  const clientExperience = {
    cards: [
      pending('company_experience', 'Company Experience', 'reviews carry one rating per booking today; separating company from talent is the review-types work (handoff §13)'),
      card('talent_experience', 'Talent Experience', avgRating(cur.reviews), 'average verified rating in the period (the one rating clients give today)', { unit: '/5' }),
      card('review_volume', 'Review Volume', cur.reviews.length, 'verified reviews in the period', {}),
      card('cancellation_rate', 'Cancellation Rate', cancelRate(cur), 'same rule as the executive card', { percent: true }),
      card('refund_rate', 'Refund Rate', pct(cur.refunds.length, cur.paid.length), 'cash refunds ÷ completed payments in the period', { percent: true }),
      card('negative_reviews', 'Negative Reviews', negative, 'verified reviews rated 2 or below in the period', {})
    ],
    issueCategories: pending('issue_categories', 'Issue Categories', 'nothing tags feedback as communication / talent / delivery / billing yet; categories arrive with editing issue notes and review types (Track C)'),
    reviewTrend: weeklyReviewTrend(range, cur.reviews)
  }

  return { range, prior, generatedFor: today, executive, sales, revenue, operations, talentHealth, clientExperience, geographic: markets, counts: { bookings: real.length, created: cur.created.length, events: cur.events.length } }
}

/** Shares of a list by a label function, in a fixed order when given. */
export function mix(items, labelOf, order = null) {
  const counts = new Map()
  for (const it of items) { const k = labelOf(it); counts.set(k, (counts.get(k) || 0) + 1) }
  const keys = order ? [...order, ...[...counts.keys()].filter(k => !order.includes(k))] : [...counts.keys()].sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0))
  const total = items.length
  return keys.filter(k => order || counts.has(k)).map(k => ({ label: k, count: counts.get(k) || 0, percent: total ? Math.round(((counts.get(k) || 0) / total) * 100) : 0 }))
}

/** Weekly buckets across the range: new bookings and cash collected. */
/**
 * The trend's points, sized to the period (Trera, Oct 2026: MTD and QTD in the first week of October were one weekly
 * point, so the line drew nothing): by day up to 14 days, by week up to 120, by month beyond.
 */
export function trendBuckets(range) {
  const days = daysBetween(range.from, range.to) + 1
  const out = []
  if (days <= 14) {
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) out.push({ from: d, to: d, unit: 'day' })
  } else if (days <= 120) {
    for (let start = range.from; start <= range.to; start = addDays(start, 7)) out.push({ from: start, to: addDays(start, 6) < range.to ? addDays(start, 6) : range.to, unit: 'week' })
  } else {
    for (let start = range.from; start <= range.to;) {
      const [y, m] = start.split('-').map(Number)
      const next = `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`
      const end = addDays(next, -1) < range.to ? addDays(next, -1) : range.to
      out.push({ from: start, to: end, unit: 'month' })
      start = next
    }
  }
  return out
}

export function weeklyTrend(range, created, paid) {
  const weeks = []
  for (const { from: start, to: end, unit } of trendBuckets(range)) {
    weeks.push({
      from: start, to: end, unit,
      bookings: created.filter(b => inRange(b.created_at, { from: start, to: end })).length,
      cashCents: Math.round(sum(paid.filter(p => inRange(p.payment_date || p.created_at, { from: start, to: end })), p => p.amount) * 100)
    })
  }
  return weeks
}

export function weeklyReviewTrend(range, reviews) {
  const weeks = []
  for (const { from: start, to: end, unit } of trendBuckets(range)) {
    const rs = reviews.filter(r => inRange(r.created_at, { from: start, to: end }))
    weeks.push({ from: start, to: end, unit, count: rs.length, average: round1(mean(rs.map(r => Number(r.rating)))) })
  }
  return weeks
}

/**
 * Add-on revenue in the period: post-booking rows (booking_addons, priced) plus the add-ons bought at checkout on
 * bookings created in the period (personalization_data.addons, price × qty, free promo items at $0).
 */
export function addonRevenueTable(createdBookings, addonRowsInPeriod) {
  const acc = new Map()
  const add = (name, cents) => { const k = String(name || 'Add-on'); const cur = acc.get(k) || { name: k, booked: 0, cents: 0 }; cur.booked += 1; cur.cents += cents; acc.set(k, cur) }
  for (const r of addonRowsInPeriod) add(r.addon?.name || r.addon?.code || 'Add-on', Number(r.total_price_cents || 0))
  for (const b of createdBookings) {
    const list = Array.isArray(b.personalization_data?.addons) ? b.personalization_data.addons : []
    for (const a of list) {
      if (a?.source && a.source !== 'checkout' && a.source !== 'promotion') continue // post-booking mirrors are counted from booking_addons
      const qty = Number(a?.qty || 1) || 1
      add(a?.title || a?.name || a?.code || a?.id, a?.isFree ? 0 : Math.round(Number(a?.price || 0) * 100) * qty)
    }
  }
  return [...acc.values()].sort((x, y) => y.cents - x.cents)
}

/** Market rows: bookings created in the period by city/state, their agreed revenue, talent nearby, and a health word. */
export function geographic(createdBookings, allBookings, activeBalances, talent, agreedCents, today, byId) {
  const talentByMarket = new Map()
  for (const t of talent) {
    if (t.status === 'Blacklisted' || !t.is_public) continue
    const k = cityKey(t.city, t.state); if (k === '|') continue
    talentByMarket.set(k, (talentByMarket.get(k) || 0) + 1)
  }
  const upcomingByMarket = new Map()
  for (const bal of activeBalances) {
    const b = byId.get(bal.booking_id); if (!b || dateOnly(b.event_date) < today) continue
    const k = cityKey(b.location_city, b.location_state); if (k === '|') continue
    upcomingByMarket.set(k, (upcomingByMarket.get(k) || 0) + 1)
  }
  const rows = new Map()
  for (const b of createdBookings) {
    const m = marketOf(b); if (!m) continue
    const k = cityKey(b.location_city, b.location_state)
    const r = rows.get(k) || { market: m, key: k, bookings: 0, revenueCents: 0 }
    r.bookings += 1; r.revenueCents += agreedCents(b); rows.set(k, r)
  }
  const out = [...rows.values()].map(r => {
    const eligible = talentByMarket.get(r.key) || 0
    const upcoming = upcomingByMarket.get(r.key) || 0
    const ratio = upcoming ? eligible / upcoming : eligible
    const health = upcoming === 0 ? 'healthy' : ratio >= 3 ? 'healthy' : ratio >= 1 ? 'watch' : 'recruit'
    return { ...r, avgValueCents: r.bookings ? Math.round(r.revenueCents / r.bookings) : 0, eligibleTalent: eligible, upcomingJobs: upcoming, health }
  })
  return out.sort((a, b) => b.bookings - a.bookings || b.revenueCents - a.revenueCents)
}

/** Rows → CSV text for the Export buttons. */
export function toCsv(rows, columns) {
  const esc = v => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
  return [columns.map(c => esc(c.label)).join(','), ...rows.map(r => columns.map(c => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(','))].join('\n')
}
